/**
 * Sprint 208A Path A — Parent-First Signup
 *
 * Three-step flow on one page (no route changes between steps):
 *   1. Rapid 3-field form (Name, Email, Phone)
 *   2. HiBFF Commitment Charter modal with typed signature
 *   3. Magic link copy view (single "Copy Invite Link" button — scope lock 1d)
 *
 * On Step 3 the parent is auto-logged-in (we store the JWT) so a "Go to
 * Parent Dashboard" button on the success view drops them straight into
 * /parent-dashboard.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Phone, User as UserIcon, Loader2, Copy, Check, ArrowRight, FileText } from 'lucide-react';
import { apiClient } from '../../api/client';

const CHARTER_TEXT_V1 = (
  "HiBFF Commitment Charter. We agree to use HiBFF as a private space to " +
  "build social fitness — never as social media. Conversations between " +
  "the teen and any AvA are confidential; the parent sees activity and " +
  "progress only. The teen will not share personal contact details inside " +
  "the app, will respect the 9-domain safety guardrails, and will follow " +
  "the 20-challenge journey at their own pace. The parent will respect " +
  "the teen's private practice space and use the parent dashboard to " +
  "celebrate progress, not police content. We commit, together, to " +
  "courage that builds confidence and confidence that builds character."
);

const Step = ({ active, done, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
      done ? 'bg-green-500 text-white'
      : active ? 'bg-purple-600 text-white'
      : 'bg-gray-200 text-gray-500'
    }`}>{done ? <Check size={14} /> : label[0]}</div>
    <span className={`text-xs font-medium ${active ? 'text-purple-700' : done ? 'text-green-700' : 'text-gray-500'}`}>{label}</span>
  </div>
);

const ParentFirstSignup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', phone: '', teen_handle: '' });
  const [typedSignature, setTypedSignature] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { parent_jwt, magic_link, invite_message }
  const [copied, setCopied] = useState(false);

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
  const magicLinkFullUrl = result ? `${window.location.origin}${result.magic_link.magic_link_path}` : '';

  const submitForm = (e) => {
    e?.preventDefault();
    setError(null);
    if (!form.name || !form.email || !form.phone) {
      setError('Please fill name, email, and phone.');
      return;
    }
    setStep(2);
  };

  const submitCharter = async () => {
    setError(null);
    if (typedSignature.trim().length < 2) {
      setError('Please type your full name as your digital signature.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiClient.request('/api/onboarding/parent-first/register-and-sign', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          typed_signature: typedSignature.trim(),
          teen_handle: form.teen_handle.trim() || undefined,
        }),
      });
      // Auto-login the parent so the magic link issuance + dashboard entry are seamless.
      if (data.parent_jwt) {
        localStorage.setItem('hibff_auth_token', data.parent_jwt);
      }
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(magicLinkFullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      // Fallback — select-all on a hidden input is overkill; the link is also visible on-screen.
    }
  };

  const copyInviteMessage = async () => {
    const msg = (result?.invite_message || '').replace(result?.magic_link?.magic_link_path || '', magicLinkFullUrl);
    try {
      await navigator.clipboard.writeText(msg);
      setCopied('message');
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl" data-testid="parent-first-signup">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="parent-first-steps">
          <Step active={step === 1} done={step > 1} label="Register" />
          <div className="w-12 h-px bg-gray-300" />
          <Step active={step === 2} done={step > 2} label="Sign Charter" />
          <div className="w-12 h-px bg-gray-300" />
          <Step active={step === 3} done={false} label="Invite Teen" />
        </div>

        {step === 1 && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10" data-testid="parent-first-step1">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Shield size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Set up your HiBFF family</h1>
              <p className="text-sm text-gray-500 mt-1">3 fields. One signature. Then send your teen the invite.</p>
            </div>
            <form onSubmit={submitForm} className="space-y-5">
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Your name</span>
                <div className="mt-1 relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                    placeholder="First and last name"
                    data-testid="parent-first-name"
                    required
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</span>
                <div className="mt-1 relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                    placeholder="you@email.com"
                    data-testid="parent-first-email"
                    required
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Phone</span>
                <div className="mt-1 relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                    placeholder="+1 555 123 4567"
                    data-testid="parent-first-phone"
                    required
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Teen&apos;s email or phone <span className="text-gray-400 font-normal normal-case">(optional)</span></span>
                <input
                  type="text"
                  value={form.teen_handle}
                  onChange={e => setForm({ ...form, teen_handle: e.target.value })}
                  className="mt-1 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                  placeholder="So your teen sees who the invite is from"
                  data-testid="parent-first-teen-handle"
                />
              </label>
              {error && <p className="text-sm text-red-600" data-testid="parent-first-error">{error}</p>}
              <button
                type="submit"
                className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center justify-center gap-2"
                data-testid="parent-first-continue"
              >
                Continue to Charter <ArrowRight size={16} />
              </button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10" data-testid="parent-first-step2-charter">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FileText size={22} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">HiBFF Commitment Charter</h2>
                <p className="text-xs text-gray-500">Sign to set the family expectations for the practice space</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 via-white to-pink-50 border border-amber-200 rounded-2xl p-5 text-sm leading-relaxed text-gray-700 mb-6 max-h-72 overflow-y-auto" data-testid="charter-text">
              {CHARTER_TEXT_V1}
            </div>
            <label className="block mb-2">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Parent digital signature</span>
              <input
                type="text"
                value={typedSignature}
                onChange={e => setTypedSignature(e.target.value)}
                placeholder="Type your full name"
                className="mt-1 w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none text-2xl"
                style={{ fontFamily: 'Caveat, "Brush Script MT", cursive', fontStyle: 'italic' }}
                data-testid="parent-first-signature"
              />
            </label>
            <p className="text-xs text-gray-400 mb-5">Typing your name above is your legal digital signature on this charter.</p>
            {error && <p className="text-sm text-red-600 mb-3" data-testid="parent-first-error-2">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-5 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50"
                data-testid="parent-first-back"
              >Back</button>
              <button
                onClick={submitCharter}
                disabled={submitting}
                className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="parent-first-sign-submit"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Sign & Generate Invite <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-testid="parent-first-step3-link">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center">
              <Check size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Charter signed</h2>
            <p className="text-sm text-gray-500 mb-6">Send the invite link below to your teen via your preferred channel.</p>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4 text-left break-all text-sm font-mono text-gray-700" data-testid="parent-first-magic-link-display">
              {magicLinkFullUrl}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={copyInviteLink}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center justify-center gap-2"
                data-testid="parent-first-copy-link"
              >
                {copied === true ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Invite Link</>}
              </button>
              <button
                onClick={copyInviteMessage}
                className="flex-1 py-3 border-2 border-purple-200 text-purple-700 font-semibold rounded-xl hover:bg-purple-50 inline-flex items-center justify-center gap-2"
                data-testid="parent-first-copy-message"
              >
                {copied === 'message' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Full Message</>}
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-4">Link expires in 7 days. Paste it into iMessage / WhatsApp / email — your teen will sign the charter and start practising.</p>
            <button
              onClick={() => navigate('/parent-dashboard')}
              className="text-sm text-purple-600 font-semibold hover:text-purple-800"
              data-testid="parent-first-go-dashboard"
            >Go to my Parent Dashboard →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentFirstSignup;
